// Réseau Social - Design Premium 10/10
// Style Instagram/TikTok optimisé AbracadaBati

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal,
  Play, Pause, Volume2, VolumeX, MapPin, ChevronLeft, ChevronRight,
  Plus, Home, Compass, Video, User, Bell, Search, ArrowLeft,
  Package, Tag, Store, ExternalLink, Map, Layers, Hammer, ShoppingBag,
  Zap, Droplets, TreeDeciduous, Paintbrush, HardHat, Ruler, Flame,
  Building2, ShieldCheck, Wind, Newspaper, Users, Briefcase, Calendar,
  Network, Award, Menu, Wrench, ChevronDown, Camera, SlidersHorizontal,
  X, AlertTriangle, Info, Euro, HelpCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import UnifiedHeader from '../../../components/layout/UnifiedHeader';
import MainNavigation from '../../../components/layout/MainNavigation';
import UniversalBanner from '../../../components/layout/UniversalBanner';
import BetaBanner from '../../../components/ui/BetaBanner';
import PublishModal from '../../../components/reseau/PublishModal';
import { TrustBadgeIcon } from '../../../components/badges/TrustBadge';
import { FounderBadgeIcon } from '../../../components/badges/FounderBadge';

import { API_URL } from '../../../config/api';

// 15 Catégories Métiers pour le réseau social
const METIERS_CATEGORIES = [
  { id: 'electricite', label: 'Électricité', icon: Zap, color: '#EAB308' },
  { id: 'plomberie', label: 'Plomberie', icon: Droplets, color: '#0EA5E9' },
  { id: 'jardin', label: 'Jardin', icon: TreeDeciduous, color: '#059669' },
  { id: 'piscine', label: 'Piscine', icon: Droplets, color: '#3B82F6' },
  { id: 'peinture', label: 'Peinture', icon: Paintbrush, color: '#EC4899' },
  { id: 'maconnerie', label: 'Maçonnerie', icon: HardHat, color: '#EA580C' },
  { id: 'renovation', label: 'Rénovation', icon: Hammer, color: '#6366F1' },
  { id: 'carrelage', label: 'Carrelage', icon: Layers, color: '#10B981' },
  { id: 'menuiserie', label: 'Menuiserie', icon: Ruler, color: '#D97706' },
  { id: 'chauffage', label: 'Chauffage', icon: Flame, color: '#EF4444' },
  { id: 'couverture', label: 'Couverture', icon: Building2, color: '#8B5CF6' },
  { id: 'isolation', label: 'Isolation', icon: ShieldCheck, color: '#22C55E' },
  { id: 'climatisation', label: 'Climatisation', icon: Wind, color: '#06B6D4' },
  { id: 'facade', label: 'Façade', icon: Building2, color: '#A855F7' },
  { id: 'solaire', label: 'Solaire', icon: Zap, color: '#F59E0B' },
];

// Types de contenu pour le filtre
const CONTENT_TYPES = [
  { id: 'all', label: 'Tout', icon: Layers },
  { id: 'chantier', label: 'Chantiers', icon: HardHat },
  { id: 'produit', label: 'Produits', icon: Package },
  { id: 'conseil', label: 'Conseils', icon: Newspaper },
  { id: 'avant_apres', label: 'Avant/Après', icon: Camera },
];

// ══════════════════════════════════════════════════════════════════════════════
// DONNÉES DE TEST - PRÉOUVERTURE RÉSEAU SOCIAL
// ══════════════════════════════════════════════════════════════════════════════

const MOCK_POSTS = [
  {
    id: 'post-1',
    author: { 
      display_name: 'Plomberie Martin', 
      username: 'plomberie_martin', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'P',
      avatar_color: '#3B82F6'
    },
    author_type: 'artisan',
    category: 'realisation',
    metier: 'Plombier',
    content: '🔧 Installation complète d\'une salle de bain : baignoire îlot, double vasque, robinetterie thermostatique. Résultat impeccable ! #plomberie #salledebain #paris',
    photos: ['https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=800'],
    city: 'Paris',
    likes_count: 127,
    comments_count: 23,
    created_at: new Date(Date.now() - 2 * 3600000).toISOString(),
    type: 'image'
  },
  {
    id: 'post-2',
    author: { 
      display_name: 'Élec Pro Sud', 
      username: 'elec_pro_sud', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'E',
      avatar_color: '#F59E0B'
    },
    author_type: 'artisan',
    category: 'avant_apres',
    metier: 'Électricien',
    content: '⚡ Avant/Après tableau électrique — passage de 8 à 22 disjoncteurs, mise aux normes NF C 15-100. #electricite #tableauelectrique #normes',
    photos: ['https://images.unsplash.com/photo-1621905251189-08b45d6a269e?w=800'],
    city: 'Marseille',
    likes_count: 89,
    comments_count: 15,
    created_at: new Date(Date.now() - 3 * 3600000).toISOString(),
    type: 'reel',
    reel_duration: 7
  },
  {
    id: 'post-3',
    author: { 
      display_name: 'Maçonnerie Dupont', 
      username: 'maconnerie_dupont', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'M',
      avatar_color: '#6B7280'
    },
    author_type: 'artisan',
    category: 'chantier',
    metier: 'Maçon',
    content: '🧱 Réfection façade en pierre de taille — 180m² traités, jointoiement à la chaux naturelle. 3 semaines de chantier. #maconnerie #pierredetaille #renovation',
    photos: ['https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800'],
    city: 'Lyon',
    likes_count: 198,
    comments_count: 34,
    created_at: new Date(Date.now() - 4 * 3600000).toISOString(),
    type: 'image'
  },
  {
    id: 'post-4',
    author: { 
      display_name: 'Carrelage Expert', 
      username: 'carrelage_expert', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'C',
      avatar_color: '#10B981'
    },
    author_type: 'artisan',
    category: 'technique',
    metier: 'Carreleur',
    content: '💡 Double encollage sur grand format 60x120 — la technique qui évite les sonnettes. Partagez à vos confrères ! #carrelage #grandformat #conseil #pro',
    photos: ['https://images.unsplash.com/photo-1615971677499-5467cbab01c0?w=800'],
    city: 'Nantes',
    likes_count: 312,
    comments_count: 45,
    created_at: new Date(Date.now() - 5 * 3600000).toISOString(),
    type: 'reel',
    reel_duration: 8
  },
  {
    id: 'post-5',
    author: { 
      display_name: 'Peinture Renov', 
      username: 'peinture_renov', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'P',
      avatar_color: '#EC4899'
    },
    author_type: 'artisan',
    category: 'realisation',
    metier: 'Peintre',
    content: '🎨 Rénovation complète appartement 95m² — enduit, impression, 2 couches finition velours. Teinte Benjamin Moore Elephant Tusk. #peinture #renovation #bordeaux',
    photos: ['https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=800'],
    city: 'Bordeaux',
    likes_count: 234,
    comments_count: 41,
    created_at: new Date(Date.now() - 6 * 3600000).toISOString(),
    type: 'image'
  },
  {
    id: 'post-6',
    author: { 
      display_name: 'Menuiserie Boisé', 
      username: 'menuiserie_boise', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'M',
      avatar_color: '#92400E'
    },
    author_type: 'artisan',
    category: 'realisation',
    metier: 'Menuisier',
    content: '🪵 Fabrication et pose d\'un escalier bois massif suspendu — chêne huilé, limon central acier, 14 marches. 100% sur mesure. #menuiserie #escalier #chene #artisan',
    photos: ['https://images.unsplash.com/photo-1616401784845-180882ba9ba8?w=800'],
    city: 'Strasbourg',
    likes_count: 456,
    comments_count: 67,
    created_at: new Date(Date.now() - 7 * 3600000).toISOString(),
    type: 'image'
  },
  {
    id: 'post-7',
    author: { 
      display_name: 'Toiture Leblanc', 
      username: 'toiture_leblanc', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'T',
      avatar_color: '#1E3A5F'
    },
    author_type: 'artisan',
    category: 'chantier',
    metier: 'Couvreur',
    content: '🏠 Remplacement toiture complète 280m² en timelapse — dépose anciennes tuiles, lattage, pose tuiles terre cuite. 4 jours d\'équipe ! #couverture #toiture #rennes',
    photos: ['https://images.unsplash.com/photo-1632778149955-e80f8ceca2e8?w=800'],
    city: 'Rennes',
    likes_count: 178,
    comments_count: 32,
    created_at: new Date(Date.now() - 8 * 3600000).toISOString(),
    type: 'reel',
    reel_duration: 10
  },
  {
    id: 'post-8',
    author: { 
      display_name: 'Chauf\'Expert', 
      username: 'chauf_expert', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'C',
      avatar_color: '#991B1B'
    },
    author_type: 'artisan',
    category: 'avant_apres',
    metier: 'Chauffagiste',
    content: '🔥 Remplacement chaudière fioul → pompe à chaleur air/eau. Économies estimées : -65% sur la facture énergétique. Éligible MaPrimeRénov\'. #chauffage #pompeachaleur #renovation',
    photos: ['https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=800'],
    city: 'Toulouse',
    likes_count: 267,
    comments_count: 48,
    created_at: new Date(Date.now() - 10 * 3600000).toISOString(),
    type: 'image'
  },
  {
    id: 'post-9',
    author: { 
      display_name: 'Piscines Azur', 
      username: 'piscines_azur', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'P',
      avatar_color: '#06B6D4'
    },
    author_type: 'artisan',
    category: 'realisation',
    metier: 'Pisciniste',
    content: '🌊 Mise en eau de notre dernière piscine béton 10x4m — finition carrelage mosaïque bleu nuit, éclairage LED RGB. Le rêve ! #piscine #pisciniste #nice',
    photos: ['https://images.unsplash.com/photo-1575429198097-0414ec08e8cd?w=800'],
    city: 'Nice',
    likes_count: 389,
    comments_count: 56,
    created_at: new Date(Date.now() - 12 * 3600000).toISOString(),
    type: 'reel',
    reel_duration: 6
  },
  {
    id: 'post-10',
    author: { 
      display_name: 'Jardins & Création', 
      username: 'jardins_creation', 
      is_verified: true,
      profile_photo: null,
      avatar_letter: 'J',
      avatar_color: '#22C55E'
    },
    author_type: 'artisan',
    category: 'realisation',
    metier: 'Paysagiste',
    content: '🌿 Création terrasse bois IPE 45m² + jardin paysager — éclairage LED intégré, pergola bioclimatique, gazon synthétique. #paysagiste #terrasse #jardin #ipe',
    photos: ['https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=800'],
    city: 'Toulouse',
    likes_count: 345,
    comments_count: 52,
    created_at: new Date(Date.now() - 14 * 3600000).toISOString(),
    type: 'image'
  }
];

export { MOCK_POSTS };

// ═══════════════════════════════════════════════════════════════
// Composant Tags Produits sur Image (Affichage dans le Feed)
// ═══════════════════════════════════════════════════════════════
function ImageWithProductTags({ imageUrl, tags = [], onTagClick }) {
  const [showTags, setShowTags] = useState(true);
  
  if (!tags || tags.length === 0) {
    return <img src={imageUrl} alt="" className="w-full h-full object-cover" />;
  }
  
  return (
    <div 
      className="relative w-full h-full cursor-pointer"
      onClick={() => setShowTags(!showTags)}
    >
      <img src={imageUrl} alt="" className="w-full h-full object-cover" />
      
      {/* Indicateur de tags */}
      <div className="absolute bottom-3 left-3 z-10">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 rounded-full text-white text-xs">
          <ShoppingBag size={12} />
          <span>{tags.length} produit{tags.length > 1 ? 's' : ''}</span>
        </div>
      </div>
      
      {/* Tags sur l'image */}
      <AnimatePresence>
        {showTags && tags.map((tag, index) => (
          <motion.div
            key={tag.id || index}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ delay: index * 0.05 }}
            className="absolute z-20"
            style={{ 
              left: `${tag.x_percent}%`, 
              top: `${tag.y_percent}%`,
              transform: 'translate(-50%, -100%)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onTagClick?.(tag);
            }}
          >
            {/* Point indicateur */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full">
              <motion.div 
                className="w-2.5 h-2.5 bg-white rounded-full shadow-lg ring-2 ring-blue-500"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              />
            </div>
            
            {/* Bulle tag */}
            <Link 
              to={`/produits/${tag.product_id}`}
              className="block bg-white rounded-lg shadow-xl px-2.5 py-1.5 min-w-[100px] hover:scale-105 transition-transform"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-1.5">
                <ShoppingBag size={12} className="text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-[11px] leading-tight truncate">
                    {tag.product_name}
                  </p>
                  {tag.product_price && (
                    <p className="text-blue-600 font-bold text-[10px]">
                      {tag.product_price.toFixed(2)} €
                    </p>
                  )}
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

// Filtres de catégorie pour le feed
const FEED_FILTERS = [
  { id: 'all', label: 'Tous', icon: Home },
  { id: 'chantier', label: 'Chantiers', icon: Hammer },
  { id: 'produit', label: 'Produits', icon: Package },
  { id: 'conseil', label: 'Conseils', icon: Layers },
  { id: 'avant_apres', label: 'Avant/Après', icon: Compass },
];

// Composant pour afficher les produits liés
function LinkedProductsSection({ products, services }) {
  const hasProducts = products && products.length > 0;
  const hasServices = services && services.length > 0;
  
  if (!hasProducts && !hasServices) return null;
  
  return (
    <div className="px-4 pb-3 border-t border-gray-100 mt-2 pt-3">
      {/* Produits utilisés */}
      {hasProducts && (
        <div className="mb-3">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Package size={12} />
            Produits utilisés
          </h4>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {products.map((product, idx) => (
              <Link
                key={product.id || idx}
                to={`/produits/${product.product_id}`}
                className="flex-shrink-0 flex items-center gap-2 bg-gray-50 hover:bg-gray-100 rounded-lg p-2 pr-3 transition-colors group"
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-8 h-8 object-contain" />
                  ) : (
                    <Package size={16} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-900 group-hover:text-[#2F4EA2] line-clamp-1">
                    {product.name}
                  </p>
                  {product.price > 0 && (
                    <p className="text-[10px] text-gray-500">{product.price.toFixed(2)} €</p>
                  )}
                </div>
                <ExternalLink size={12} className="text-gray-400 group-hover:text-[#2F4EA2] ml-1" />
              </Link>
            ))}
          </div>
        </div>
      )}
      
      {/* Prestations associées */}
      {hasServices && (
        <div>
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
            <Hammer size={12} />
            Prestation associée
          </h4>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {services.map((service, idx) => (
              <Link
                key={service.id || idx}
                to={`/artisans`}
                className="flex-shrink-0 flex items-center gap-2 bg-blue-50 hover:bg-blue-100 rounded-lg p-2 pr-3 transition-colors group"
              >
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <Hammer size={16} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-900 group-hover:text-blue-600 line-clamp-1">
                    {service.name}
                  </p>
                  {service.price > 0 && (
                    <p className="text-[10px] text-gray-500">À partir de {service.price.toFixed(2)} €</p>
                  )}
                </div>
                <ExternalLink size={12} className="text-gray-400 group-hover:text-blue-500 ml-1" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Badge de catégorie
function CategoryBadge({ category }) {
  if (!category) return null;
  
  const config = {
    chantier: { label: 'Chantier', bg: 'bg-orange-100', text: 'text-orange-700' },
    produit: { label: 'Produit', bg: 'bg-blue-100', text: 'text-blue-700' },
    conseil: { label: 'Conseil', bg: 'bg-green-100', text: 'text-green-700' },
    avant_apres: { label: 'Avant/Après', bg: 'bg-purple-100', text: 'text-purple-700' },
    realisation: { label: 'Réalisation', bg: 'bg-emerald-100', text: 'text-emerald-700' },
    technique: { label: 'Technique', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  };
  
  const cat = config[category];
  if (!cat) return null;
  
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${cat.bg} ${cat.text}`}>
      {cat.label}
    </span>
  );
}

// Post Card Component - Premium Design avec badge TEST
function PostCard({ post, onLike, onSave, currentUserId, index }) {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  
  const isTest = post.id?.startsWith('post-');

  const handleLike = async () => {
    if (!currentUserId) {
      toast.error('Connectez-vous pour aimer ce post');
      return;
    }
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    onLike?.(post.id, !isLiked);
  };

  const handleSave = async () => {
    if (!currentUserId) {
      toast.error('Connectez-vous pour sauvegarder');
      return;
    }
    setIsSaved(!isSaved);
    onSave?.(post.id, !isSaved);
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} j`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="bg-white rounded-2xl overflow-hidden mb-6 relative"
      style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
    >
      {/* Badge TEST pour préouverture */}
      {isTest && (
        <div className="absolute top-3 right-3 z-20 bg-amber-500 text-white px-2.5 py-1 rounded-lg text-xs font-bold flex items-center gap-1 shadow-lg">
          <Info size={12} />
          TEST
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <Link to={`/reseau/profil/${post.author?.username || post.author_name}`} className="flex items-center gap-3">
          {post.author?.profile_photo ? (
            <img
              src={post.author.profile_photo}
              alt={post.author.display_name}
              className="w-11 h-11 rounded-full object-cover ring-2 ring-gray-100"
            />
          ) : (
            <div 
              className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm ring-2 ring-gray-100"
              style={{ backgroundColor: post.author?.avatar_color || '#1E40AF' }}
            >
              {post.author?.avatar_letter || (post.author?.display_name || post.author_name)?.[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-gray-900">{post.author?.display_name || post.author_name}</span>
              {(post.author?.is_verified || post.author_type === 'artisan') && (
                <svg className="w-4 h-4 text-[#1E40AF]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {post.author?.is_founder && <FounderBadgeIcon founderNumber={post.author.founder_number} size={16} />}
              {post.author?.trust_level && <TrustBadgeIcon trustLevel={post.author.trust_level} size={16} />}
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <CategoryBadge category={post.category} />
              {post.city && (
                <span className="flex items-center gap-0.5 text-xs">
                  <MapPin size={10} />
                  {post.city}
                </span>
              )}
              <span>• {formatTimeAgo(post.created_at)}</span>
            </div>
          </div>
        </Link>
        <div className="flex items-center gap-1">
          {/* Bouton voir sur la carte si chantier géolocalisé */}
          {post.show_on_map && post.latitude && post.longitude && (
            <motion.button 
              onClick={() => navigate(`/reseau/chantiers?focus=${post.id}`)}
              className="w-9 h-9 rounded-full flex items-center justify-center bg-blue-50 hover:bg-blue-100 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Voir sur la carte"
            >
              <Map size={16} className="text-blue-600" />
            </motion.button>
          )}
          <motion.button 
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <MoreHorizontal size={20} className="text-gray-500" />
          </motion.button>
        </div>
      </div>

      {/* Content */}
      {post.content && (
        <div className="px-4 pb-3">
          <p className="text-gray-800 whitespace-pre-line leading-relaxed">
            {post.content}
          </p>
        </div>
      )}

      {/* Media avec Tags Produits */}
      {post.photos && post.photos.length > 0 && (
        <div className="relative">
          <div className="aspect-[4/3] bg-gray-100">
            {/* Image avec tags produits si disponibles */}
            <ImageWithProductTags
              imageUrl={post.photos[currentMediaIndex]}
              tags={post.product_tags?.filter(t => t.media_index === currentMediaIndex) || 
                    (post.media?.[currentMediaIndex]?.product_tags) ||
                    (currentMediaIndex === 0 ? post.product_tags : [])}
              onTagClick={(tag) => navigate(`/produits/${tag.product_id}`)}
            />
          </div>
          
          {/* Badge REEL avec durée */}
          {post.type === 'reel' && post.reel_duration && (
            <div className="absolute bottom-3 left-3 z-10 bg-black/70 text-white px-2 py-1 rounded-lg text-xs font-bold flex items-center gap-1.5">
              <Play size={12} fill="white" />
              0:{post.reel_duration.toString().padStart(2, '0')}
            </div>
          )}
          
          {/* Navigation arrows */}
          {post.photos.length > 1 && (
            <>
              {currentMediaIndex > 0 && (
                <motion.button
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center"
                  onClick={() => setCurrentMediaIndex(prev => prev - 1)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronLeft size={20} className="text-gray-700" />
                </motion.button>
              )}
              {currentMediaIndex < post.photos.length - 1 && (
                <motion.button
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm shadow-lg flex items-center justify-center"
                  onClick={() => setCurrentMediaIndex(prev => prev + 1)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronRight size={20} className="text-gray-700" />
                </motion.button>
              )}
              
              {/* Dots indicator */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {post.photos.map((_, idx) => (
                  <div 
                    key={idx}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      idx === currentMediaIndex ? 'bg-white w-4' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <motion.button 
              onClick={handleLike}
              className="flex items-center gap-2 group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Heart 
                size={24} 
                className={`transition-colors ${isLiked ? 'text-red-500 fill-red-500' : 'text-gray-600 group-hover:text-red-500'}`}
              />
              <span className={`font-semibold text-sm ${isLiked ? 'text-red-500' : 'text-gray-600'}`}>
                {likesCount}
              </span>
            </motion.button>
            
            <Link to={`/reseau/post/${post.id}`} className="flex items-center gap-2 group">
              <MessageCircle size={24} className="text-gray-600 group-hover:text-[#2F4EA2] transition-colors" />
              <span className="font-semibold text-sm text-gray-600">{post.comments_count || 0}</span>
            </Link>
            
            <motion.button 
              className="group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Share2 size={22} className="text-gray-600 group-hover:text-[#2F4EA2] transition-colors" />
            </motion.button>
          </div>
          
          <motion.button 
            onClick={handleSave}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Bookmark 
              size={24} 
              className={`transition-colors ${isSaved ? 'text-[#2F4EA2] fill-[#2F4EA2]' : 'text-gray-600 hover:text-[#2F4EA2]'}`}
            />
          </motion.button>
        </div>
      </div>
      
      {/* Section Produits/Prestations liés */}
      <LinkedProductsSection 
        products={post.linked_products} 
        services={post.linked_services} 
      />
      
      {/* Bouton voir boutique si chantier */}
      {post.show_on_map && post.shop && (
        <div className="px-4 pb-4">
          <Link 
            to={`/boutique/${post.shop.slug}`}
            className="flex items-center justify-center gap-2 w-full py-2.5 bg-gradient-to-r from-[#2F4EA2] to-[#1E3A8A] text-white rounded-xl font-medium text-sm hover:opacity-90 transition-opacity"
          >
            <Store size={16} />
            Voir la boutique de l'artisan
          </Link>
        </div>
      )}
    </motion.article>
  );
}

// Stories Section - Premium
function StoriesSection({ profiles }) {
  if (!profiles || profiles.length === 0) return null;
  
  return (
    <div className="bg-white mb-6 rounded-2xl" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
      <div className="px-4 py-4">
        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {profiles.map((profile, idx) => (
            <Link
              key={profile.id || idx}
              to={`/reseau/profil/${profile.username}`}
              className="flex flex-col items-center gap-2 flex-shrink-0"
            >
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-16 h-16 rounded-full p-0.5 bg-gradient-to-br from-[#FF6A00] via-[#F97316] to-[#FBBF24]">
                  <div className="w-full h-full rounded-full bg-white p-0.5">
                    {profile.profile_photo ? (
                      <img
                        src={profile.profile_photo}
                        alt={profile.display_name}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg">
                        {profile.display_name?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
              <span className="text-xs text-gray-700 font-medium truncate max-w-[64px]">
                {profile.username || profile.display_name?.split(' ')[0]}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// Sidebar - Premium
function Sidebar({ salons, onPublishClick }) {
  return (
    <div className="hidden lg:block w-80 flex-shrink-0">
      <div className="sticky top-24 space-y-6">
        {/* Salons thématiques */}
        <div className="bg-white rounded-2xl p-5" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <h3 className="font-bold text-gray-900 mb-4">Salons populaires</h3>
          <div className="space-y-3">
            {(salons || []).slice(0, 5).map((salon, idx) => (
              <Link
                key={idx}
                to={`/reseau/salon/${salon.slug}`}
                className="flex items-center gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2F4EA2] to-[#1E40AF] flex items-center justify-center text-lg">
                  {salon.icon || '💬'}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900 group-hover:text-[#2F4EA2] transition-colors text-sm">
                    {salon.name}
                  </p>
                  <p className="text-xs text-gray-500">{salon.members_count} membres</p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/reseau/salons" className="block mt-4 text-center text-sm text-[#2F4EA2] font-semibold hover:text-[#1E40AF]">
            Voir tous les salons
          </Link>
        </div>

        {/* CTA Publier - Centré avec padding 24px */}
        <div 
          className="rounded-2xl p-6 text-center"
          style={{ background: 'linear-gradient(135deg, #FF6A00, #FF7E1F)', padding: '24px' }}
        >
          <h3 className="font-bold mb-2 text-center" style={{ fontSize: '16px', fontWeight: 700, color: '#FFFFFF' }}>Partage tes réalisations</h3>
          <p className="mb-4 text-center" style={{ fontSize: '13px', color: 'rgba(255,255,255,0.9)' }}>Montre ton savoir-faire à la communauté</p>
          <motion.button
            onClick={onPublishClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#FF6A00] rounded-xl font-semibold text-sm"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Plus size={18} />
            Publier
          </motion.button>
        </div>

        {/* ═══════════════════════════════════════════════════════════
            CARD — MEMBRE FONDATEUR (Centré avec padding 24px)
        ═══════════════════════════════════════════════════════════ */}
        <div 
          className="rounded-xl overflow-hidden text-center"
          style={{ backgroundColor: '#1E3A5F', padding: '24px' }}
        >
          {/* Badge "Places limitées" */}
          <div 
            className="inline-flex items-center gap-[5px] mb-[10px]"
            style={{ 
              backgroundColor: 'rgba(249, 115, 22, 0.15)',
              borderRadius: '20px',
              padding: '3px 10px'
            }}
          >
            <div 
              className="rounded-full"
              style={{ width: '5px', height: '5px', backgroundColor: '#F5A672' }}
            />
            <span 
              style={{ 
                fontSize: '10px', 
                fontWeight: 600, 
                color: '#F5A672',
                letterSpacing: '0.05em'
              }}
            >
              Places limitées
            </span>
          </div>

          {/* Titre */}
          <h4 
            style={{ 
              fontSize: '16px', 
              fontWeight: 700, 
              color: '#FFFFFF',
              lineHeight: 1.3,
              marginBottom: '8px'
            }}
          >
            Deviens Membre Fondateur
          </h4>

          {/* Sous-titre */}
          <p style={{ fontSize: '13px', lineHeight: 1.5, marginBottom: '12px' }}>
            <span style={{ color: '#B8D4F0' }}>CRM + boutique paramétrés en 72h.</span>
            <br />
            <span style={{ color: '#E2E8F0', fontWeight: 500 }}>2 ans d'abonnement offerts.</span>
          </p>

          {/* Liste 3 points */}
          <div className="space-y-[5px] mb-[14px]">
            {['Badge Fondateur à vie', 'Club Fondateur privé', 'Paramétrage clé en main'].map((item, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6EE7C7" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span style={{ fontSize: '11px', color: '#CBD5E1' }}>{item}</span>
              </div>
            ))}
          </div>

          {/* Bouton */}
          <Link to="/pro/fondateur" className="block">
            <button 
              className="w-full py-[10px] rounded-lg text-white font-semibold transition-colors hover:opacity-90"
              style={{ 
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.25)'
              }}
            >
              En savoir plus →
            </button>
          </Link>

          {/* Mention bas */}
          <p style={{ fontSize: '10px', color: '#4A6FA5', textAlign: 'center', marginTop: '8px' }}>
            100 places · Jusqu'au 1er Avril 2026
          </p>
        </div>
      </div>
    </div>
  );
}

// Sous-menu horizontal Réseau - Desktop/Tablet uniquement
const RESEAU_SUBMENU_ITEMS = [
  { id: 'feed', label: "Fil d'actualité", path: '/reseau' },
  { id: 'page-pro', label: 'Page Pro', path: '/reseau/pages-pro' },
  { id: 'cinema', label: 'Cinéma', path: '/reseau/reels' },
  { id: 'galerie', label: 'Galerie', path: '/reseau/galeries' },
  { id: 'salon', label: 'Salon', path: '/reseau/salons' },
  { id: 'evenement', label: 'Événement', path: '/reseau/evenements' },
];

// Main Feed Page - Premium
export default function FeedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useOutletContext() || {};
  const [posts, setPosts] = useState([]);
  const [suggestedProfiles, setSuggestedProfiles] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedMetier, setSelectedMetier] = useState(null);
  const [selectedTab, setSelectedTab] = useState('tout'); // Onglets: tout, a_cote, suivis, promos
  const [userLocation, setUserLocation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const token = localStorage.getItem('ecosystem_token');
  const isLoggedIn = !!token;

  // Gérer le changement d'onglet "À côté" - demander la géoloc
  useEffect(() => {
    if (selectedTab === 'a_cote' && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
            toast.success('Position détectée - rayon 30km');
          },
          () => {
            toast.error('Localisation désactivée - active-la pour ce filtre');
          }
        );
      }
    }
  }, [selectedTab, userLocation]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      // Construire l'URL avec les paramètres (comme mobile)
      const params = new URLSearchParams();
      params.append('tab', selectedTab);
      params.append('limit', '30');
      
      // Filtre par métier
      if (selectedMetier) {
        params.append('metier', selectedMetier);
      }
      
      // Géolocalisation pour "À côté"
      if (selectedTab === 'a_cote' && userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
        params.append('radius', '30');
      }
      
      // User ID pour "Suivis"
      const userId = localStorage.getItem('user_id') || 'demo_user';
      if (selectedTab === 'suivis') {
        params.append('user_id', userId);
      }
      
      const url = `${API_URL}/api/feed?${params.toString()}`;
      const res = await fetch(url);
      
      if (res.ok) {
        const data = await res.json();
        let items = data.items || [];
        
        // Filtrage local par catégorie si besoin
        if (selectedCategory !== 'all') {
          items = items.filter(p => p.category === selectedCategory);
        }
        
        setPosts(items);
      } else {
        // Fallback vers MOCK si API échoue
        setPosts(MOCK_POSTS);
      }
    } catch (e) {
      console.error('Error fetching feed:', e);
      setPosts(MOCK_POSTS);
    } finally {
      setLoading(false);
    }
  }, [selectedTab, selectedMetier, selectedCategory, userLocation]);

  const fetchSuggestedProfiles = async () => {
    // Profiles suggérés MOCK
    setSuggestedProfiles([]);
  };

  const fetchSalons = async () => {
    // Salons MOCK
    setSalons([]);
  };

  useEffect(() => {
    fetchFeed();
    fetchSuggestedProfiles();
    fetchSalons();
  }, [fetchFeed]);

  // Détecter l'item actif du sous-menu
  const getActiveSubmenuItem = () => {
    const currentPath = location.pathname;
    if (currentPath === '/reseau') return 'feed';
    const item = RESEAU_SUBMENU_ITEMS.find(i => i.path !== '/reseau' && currentPath.startsWith(i.path));
    return item?.id || 'feed';
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-['Inter',sans-serif]" data-testid="reseau-feed-page">
      {/* Header Unifié - Design System */}
      <UnifiedHeader onSearch={({ query }) => setSearchQuery(query)} onOpenFilters={() => setShowFilters(true)} />
      
      {/* Navigation Principale - Design System */}
      <MainNavigation />

      {/* ═══ SOUS-MENU HORIZONTAL RÉSEAU - Desktop/Tablet uniquement ═══ */}
      <div className="hidden md:block fixed top-[100px] left-0 right-0 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-2">
            {RESEAU_SUBMENU_ITEMS.map((item) => {
              const isActive = getActiveSubmenuItem() === item.id;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-[#1E3A5F] text-white'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-[#1E3A5F]'
                  }`}
                  data-testid={`submenu-${item.id}`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Spacer Mobile - 44px pour le menu horizontal fixed */}
      <div className="h-11 md:hidden" />

      {/* Spacer Desktop/Tablet - compense MainNavigation + sous-menu horizontal */}
      <div className="hidden md:block" style={{ height: '148px' }} />

      {/* ═══ HERO BANNER RÉSEAU ═══ */}
      <div className="bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.1) 10px, rgba(255,255,255,0.1) 20px)'
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-4 py-8 md:py-10 relative">
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Network size={32} className="text-yellow-300" />
              <h1 className="text-3xl md:text-4xl font-black text-white">LE RÉSEAU SOCIAL 100% BÂTIMENT</h1>
              <Network size={32} className="text-yellow-300" />
            </div>
            <p className="text-white/90 text-base mb-4">
              Découvre et partage ta passion entre <span className="font-bold text-white">particuliers</span> et <span className="font-bold text-white">pros certifiés</span>
            </p>
            <div className="inline-flex items-center gap-2 md:gap-3 bg-[#E0F2FE]/90 backdrop-blur rounded-full px-5 py-2.5 border-2 border-[#0369A1]/50">
              <span className="text-[#0C4A6E] font-bold text-sm">1 personne = 1 compte = 0 faux profil !</span>
            </div>
          </div>
        </div>
      </div>

      {/* Badges de confiance */}
      <div className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 md:gap-8 flex-wrap">
            {[
              { icon: Users, text: 'Communauté vérifiée' },
              { icon: ShieldCheck, text: 'Profils certifiés' },
              { icon: Award, text: 'Contenu de qualité' },
              { icon: Network, text: '100% Bâtiment' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-600">
                <item.icon size={18} className="text-orange-500" />
                <span className="text-xs md:text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bandeau de Conversion Universel */}
      <UniversalBanner variant="reseau" className="py-4" />

      {/* Barre de Filtres - Style unifié */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            {/* Bouton Métiers & Filtres */}
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-[#1E40AF] hover:text-[#1E40AF] transition-all"
              data-testid="btn-metiers-filtres"
            >
              <SlidersHorizontal size={18} />
              Métiers & Filtres
            </button>
            
            {/* Bouton Type de contenu */}
            <button
              onClick={() => setShowFilters(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:border-[#F97316] hover:text-[#F97316] transition-all"
              data-testid="btn-type-contenu"
            >
              <Layers size={18} />
              Type de contenu
            </button>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          LAYOUT DESKTOP - Sidebar gauche style Facebook
          Visible uniquement sur lg: (desktop)
          Sur desktop: colonnes gauche/droite fixes, colonne centrale scrolle
      ══════════════════════════════════════════════════════════════════ */}
      <div className="flex max-w-7xl mx-auto lg:h-[calc(100vh-140px)] lg:overflow-hidden">
        {/* SIDEBAR GAUCHE - Desktop uniquement - FIXE */}
        <aside className="hidden lg:block w-64 xl:w-72 flex-shrink-0 lg:h-full lg:overflow-y-auto border-r border-gray-200 bg-white">
          <DesktopSidebar 
            profile={profile} 
            selectedMetier={selectedMetier}
            setSelectedMetier={setSelectedMetier}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            onPublishClick={() => setShowPublishModal(true)}
          />
        </aside>

        {/* Main Content - SCROLLABLE sur desktop */}
        <main className="flex-1 lg:overflow-y-auto lg:h-full">
          <div className="max-w-3xl mx-auto px-4 py-4 lg:py-6 lg:px-8">
            <div className="flex gap-8">
              <div className="flex-1">
            
              {/* Beta Banner */}
              <div className="mb-4">
                <BetaBanner />
              </div>
            
              {/* Indicateur filtre métier actif */}
            {selectedMetier && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-[#1E40AF]/10 rounded-xl flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  {(() => {
                    const metier = METIERS_CATEGORIES.find(m => m.id === selectedMetier);
                    const Icon = metier?.icon || Layers;
                    return (
                      <>
                        <Icon size={16} style={{ color: metier?.color }} />
                        <span className="text-sm font-medium text-gray-700">
                          Filtré par : <strong>{metier?.label}</strong>
                        </span>
                      </>
                    );
                  })()}
                </div>
                <button 
                  onClick={() => setSelectedMetier(null)}
                  className="text-xs text-[#1E40AF] font-medium hover:underline"
                >
                  Effacer
                </button>
              </motion.div>
            )}
            
            {/* Stories */}
            <StoriesSection profiles={suggestedProfiles} />
            
            {/* Posts */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <motion.div 
                  className="w-12 h-12 border-4 border-[#2F4EA2]/20 border-t-[#2F4EA2] rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
              </div>
            ) : posts.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-12 text-center"
                style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                  <Home size={40} className="text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Bienvenue sur le réseau !</h3>
                <p className="text-gray-500 mb-6">Soyez le premier à partager du contenu</p>
                <Link to="/reseau/publier">
                  <Button className="rounded-xl" style={{ background: 'linear-gradient(135deg, #FF6A00, #FF7E1F)' }}>
                    <Plus size={18} className="mr-2" />
                    Publier
                  </Button>
                </Link>
              </motion.div>
            ) : (
              <div>
                {posts.map((post, index) => (
                  <PostCard
                    key={post.id || index}
                    post={post}
                    currentUserId={profile?.id}
                    index={index}
                  />
                ))}
              </div>
            )}
              </div>

            {/* Sidebar Droite - Desktop - FIXE */}
            <div className="hidden xl:block w-80 flex-shrink-0 xl:sticky xl:top-0 xl:h-[calc(100vh-140px)] xl:overflow-y-auto">
              <Sidebar salons={salons} onPublishClick={() => setShowPublishModal(true)} />
            </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Nav Spacer */}
      <div className="h-16 lg:hidden" />

      {/* Bottom Navigation - Now handled by GlobalMobileNav in App.js */}

      {/* Panneau de Filtres */}
      <ReseauFilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={(filters) => {
          setSelectedMetier(filters.metier || null);
          setSelectedCategory(filters.category || 'all');
        }}
        currentFilters={{
          metier: selectedMetier,
          category: selectedCategory
        }}
      />

      {/* Publish Modal */}
      <PublishModal 
        isOpen={showPublishModal} 
        onClose={() => setShowPublishModal(false)}
        isLoggedIn={isLoggedIn}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PANNEAU DE FILTRES - Style unifié
// ══════════════════════════════════════════════════════════════════════════════

function ReseauFilterPanel({ isOpen, onClose, onApplyFilters, currentFilters }) {
  const [selectedMetier, setSelectedMetier] = useState(currentFilters.metier || '');
  const [selectedCategory, setSelectedCategory] = useState(currentFilters.category || 'all');

  const applyFilters = () => {
    onApplyFilters({
      metier: selectedMetier,
      category: selectedCategory
    });
    onClose();
  };

  const resetFilters = () => {
    setSelectedMetier('');
    setSelectedCategory('all');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
          >
            {/* Header */}
            <div className="bg-[#1E40AF] px-4 py-4 flex items-center justify-between">
              <h2 className="text-white font-bold text-lg flex items-center gap-2">
                <SlidersHorizontal size={20} />
                Filtres
              </h2>
              <button onClick={onClose} className="text-white/80 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Contenu scrollable */}
            <div className="flex-1 overflow-y-auto">
              {/* Métiers */}
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Wrench size={18} className="text-[#1E40AF]" />
                  Métiers
                </h3>
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedMetier('')}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedMetier ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                    }`}
                  >
                    Tous les métiers
                  </button>
                  {METIERS_CATEGORIES.map(metier => {
                    const Icon = metier.icon;
                    return (
                      <button
                        key={metier.id}
                        onClick={() => setSelectedMetier(metier.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedMetier === metier.id ? 'bg-[#1E40AF] text-white' : 'hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={16} style={{ color: selectedMetier === metier.id ? 'white' : metier.color }} />
                        {metier.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Types de contenu */}
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Layers size={18} className="text-[#F97316]" />
                  Type de contenu
                </h3>
                <div className="space-y-1">
                  {CONTENT_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setSelectedCategory(type.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                          selectedCategory === type.id ? 'bg-[#F97316] text-white' : 'hover:bg-gray-100'
                        }`}
                      >
                        <Icon size={16} />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex gap-3">
                <button
                  onClick={resetFilters}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Réinitialiser
                </button>
                <button
                  onClick={applyFilters}
                  className="flex-1 py-3 px-4 bg-[#F97316] text-white rounded-xl font-bold hover:bg-[#EA580C] transition-colors"
                >
                  Appliquer
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BOTTOM NAVIGATION - Réseau Social Mobile
// ══════════════════════════════════════════════════════════════════════════════

function ReseauBottomNav({ onPublishClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);

  const isActive = (path) => {
    if (!path) return false;
    if (path === '/reseau') return location.pathname === '/reseau';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Menu Slide-in depuis la gauche - Style unifié */}
      <AnimatePresence>
        {showMenu && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowMenu(false)}
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 w-[85%] max-w-sm bg-white z-50 flex flex-col"
            >
              {/* Header - Charte graphique bleu */}
              <div className="bg-[#1E40AF] px-4 py-4 flex items-center justify-between">
                <h2 className="text-white font-bold text-lg flex items-center gap-2">
                  <Network size={20} />
                  Réseau Social
                </h2>
                <button onClick={() => setShowMenu(false)} className="text-white/80 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              {/* Contenu du menu */}
              <div className="flex-1 overflow-y-auto">
                {/* Section Découvrir */}
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Découvrir</h3>
                  <div className="space-y-1">
                    {[
                      { icon: Map, label: 'Carte des chantiers', path: '/reseau/chantiers', color: '#F97316' },
                      { icon: Camera, label: 'Galeries', path: '/reseau/galeries', color: '#8B5CF6' },
                      { icon: Layers, label: 'Images', path: '/reseau/images', color: '#3B82F6' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Communauté */}
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Communauté</h3>
                  <div className="space-y-1">
                    {[
                      { icon: Calendar, label: 'Événements', path: '/reseau/evenements', color: '#8B5CF6' },
                      { icon: Users, label: 'Salons de discussion', path: '/reseau/salons', color: '#10B981' },
                      { icon: Award, label: 'Artisans du mois', path: '/reseau/artisans-du-mois', color: '#F59E0B' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Mon compte */}
                <div className="p-4 border-b border-gray-100">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Mon compte</h3>
                  <div className="space-y-1">
                    {[
                      { icon: User, label: 'Mon profil', path: '/reseau/profil', color: '#1E40AF' },
                      { icon: Bookmark, label: 'Enregistrements', path: '/reseau/enregistrements', color: '#6366F1' },
                      { icon: Bell, label: 'Notifications', path: '/reseau/notifications', color: '#F97316' },
                      { icon: Heart, label: 'Mes favoris', path: '/reseau/favoris', color: '#EC4899' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Section Aide */}
                <div className="p-4">
                  <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Aide</h3>
                  <div className="space-y-1">
                    {[
                      { icon: HelpCircle, label: 'Comment ça marche', path: '/aide/reseau', color: '#6B7280' },
                      { icon: MessageCircle, label: 'Signaler un problème', path: '/aide/signaler', color: '#EF4444' },
                    ].map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.path}
                          onClick={() => { setShowMenu(false); navigate(item.path); }}
                          className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
                        >
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${item.color}15` }}>
                            <Icon size={20} style={{ color: item.color }} />
                          </div>
                          <span className="font-medium text-gray-900">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// SIDEBAR GAUCHE DESKTOP - Style Facebook
// ═══════════════════════════════════════════════════════════════
function DesktopSidebar({ profile, selectedMetier, setSelectedMetier, selectedTab, setSelectedTab, onPublishClick }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Onglets principaux du feed (comme mobile)
  const feedTabs = [
    { id: 'tout', label: 'Tout' },
    { id: 'a_cote', label: 'À côté' },
    { id: 'suivis', label: 'Suivis' },
    { id: 'promos', label: 'Promos' },
  ];
  
  // Métiers pour filtrer (identique au mobile)
  const metiersList = [
    { value: 'tous', label: 'Tous' },
    { value: 'pisciniste', label: 'Pisciniste' },
    { value: 'plombier', label: 'Plombier' },
    { value: 'electricien', label: 'Électricien' },
    { value: 'macon', label: 'Maçon' },
    { value: 'carreleur', label: 'Carreleur' },
    { value: 'menuisier', label: 'Menuisier' },
    { value: 'peintre', label: 'Peintre' },
    { value: 'couvreur', label: 'Couvreur' },
    { value: 'chauffagiste', label: 'Chauffagiste' },
    { value: 'paysagiste', label: 'Paysagiste' },
    { value: 'climaticien', label: 'Climaticien' },
  ];
  
  const mainNavItems = [
    { icon: Newspaper, label: 'Fil d\'actualité', path: '/reseau', id: 'feed' },
    { icon: Play, label: 'Reels', path: '/reseau/reels', id: 'reels' },
    { icon: Compass, label: 'Explorer', path: '/reseau/decouvrir', id: 'explorer' },
    { icon: Map, label: 'Chantiers près de toi', path: '/reseau/chantiers', id: 'chantiers' },
    { icon: Calendar, label: 'Événements', path: '/reseau/evenements', id: 'evenements' },
    { icon: Users, label: 'Salons thématiques', path: '/reseau/salons', id: 'salons' },
  ];
  
  const accountItems = [
    { icon: User, label: 'Mon profil', path: profile ? `/reseau/profil/${profile.username}` : '/reseau/connexion' },
    { icon: Bookmark, label: 'Enregistrements', path: '/reseau/enregistrements' },
    { icon: Heart, label: 'Mes likes', path: '/reseau/likes' },
    { icon: MessageCircle, label: 'Messages', path: '/reseau/messages' },
    { icon: Bell, label: 'Notifications', path: '/reseau/notifications' },
  ];
  
  const marketplaceItems = [
    { icon: Store, label: 'Artisans', path: '/artisans' },
    { icon: Package, label: 'Marketplace', path: '/produits' },
    { icon: ShoppingBag, label: 'Petites annonces', path: '/annonces' },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  return (
    <div className="py-4 px-3">
      {/* Profil utilisateur */}
      {profile ? (
        <Link 
          to={`/reseau/profil/${profile.username}`}
          className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-100 transition-colors mb-2"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E40AF] to-[#2563EB] overflow-hidden">
            {profile.profile_photo ? (
              <img src={profile.profile_photo} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-bold">
                {profile.display_name?.[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 truncate">{profile.display_name}</p>
            <p className="text-xs text-gray-500">Voir mon profil</p>
          </div>
        </Link>
      ) : (
        <div className="p-3 mb-2">
          <button
            onClick={() => navigate('/reseau/connexion')}
            className="sidebar-connect-btn w-full py-3 bg-transparent border-[1.5px] border-[#E8650A] text-[#E8650A] rounded-[10px] font-semibold text-sm hover:bg-[#FEF3E8] transition-all hover:-translate-y-[1px]"
          >
            Se connecter
          </button>
        </div>
      )}
      
      {/* Explorer le réseau - Onglets Tout/À côté/Suivis/Promos */}
      <div className="mb-4">
        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Explorer le réseau</p>
        <div className="grid grid-cols-2 gap-1 px-1 mb-3">
          {feedTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedTab === tab.id 
                  ? 'bg-orange-100 text-orange-600 border border-orange-200' 
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* Filtres métiers - scroll horizontal */}
        <div className="flex flex-wrap gap-1 px-1">
          {metiersList.map((m) => (
            <button
              key={m.value}
              onClick={() => setSelectedMetier(m.value === 'tous' ? null : m.value)}
              className={`py-1 px-2.5 rounded-full text-xs font-medium transition-colors ${
                (m.value === 'tous' && !selectedMetier) || selectedMetier === m.value
                  ? 'bg-[#1E3A5F] text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Séparateur */}
      <div className="h-px bg-gray-200 mx-3 my-3" />
      
      {/* Navigation principale */}
      <div className="mb-4">
        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Navigation</p>
        {mainNavItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.id}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${
                active 
                  ? 'bg-blue-50 text-[#1E40AF]' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon size={20} className={active ? 'text-[#1E40AF]' : 'text-gray-500'} />
              <span className={`font-medium ${active ? 'text-[#1E40AF]' : ''}`}>{item.label}</span>
            </Link>
          );
        })}
      </div>
      
      {/* Séparateur */}
      <div className="h-px bg-gray-200 mx-3 my-3" />
      
      {/* Mon compte */}
      <div className="mb-4">
        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Mon compte</p>
        {accountItems.map((item, idx) => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={idx}
              to={item.path}
              className={`flex items-center gap-3 px-3 py-2 rounded-xl transition-colors ${
                active 
                  ? 'bg-blue-50 text-[#1E40AF]' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Icon size={18} className={active ? 'text-[#1E40AF]' : 'text-gray-400'} />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </div>
      
      {/* Séparateur */}
      <div className="h-px bg-gray-200 mx-3 my-3" />
      
      {/* Marketplace */}
      <div className="mb-4">
        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Marketplace</p>
        {marketplaceItems.map((item, idx) => {
          const Icon = item.icon;
          return (
            <Link
              key={idx}
              to={item.path}
              className="flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Icon size={18} className="text-gray-400" />
              <span className="text-sm">{item.label}</span>
            </Link>
          );
        })}
      </div>
      
      {/* Séparateur */}
      <div className="h-px bg-gray-200 mx-3 my-3" />
      
      {/* Filtrer par métier */}
      <div>
        <p className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide">Filtrer par métier</p>
        <div className="space-y-0.5 max-h-48 overflow-y-auto">
          <button
            onClick={() => setSelectedMetier(null)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left ${
              !selectedMetier 
                ? 'bg-blue-50 text-[#1E40AF]' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Layers size={16} className={!selectedMetier ? 'text-[#1E40AF]' : 'text-gray-400'} />
            <span className="text-sm">Tous les métiers</span>
          </button>
          {METIERS_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const active = selectedMetier === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedMetier(active ? null : cat.id)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left ${
                  active 
                    ? 'bg-blue-50 text-[#1E40AF]' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon size={16} style={{ color: active ? '#1E40AF' : cat.color }} />
                <span className="text-sm">{cat.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Bouton Publier - Desktop */}
      <div className="mt-6 px-3">
        <button
          onClick={() => onPublishClick()}
          className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white rounded-xl font-semibold hover:opacity-90 transition-opacity"
        >
          <Plus size={20} />
          Publier
        </button>
      </div>
    </div>
  );
}
